/**
 * Auth store — holds the currently logged-in user.
 *
 * Operates in two modes, switched by `VITE_AUTH_MOCK`:
 *
 *   - **Real mode** (default, VITE_AUTH_MOCK !== "true"):
 *       login() / logout() / bootstrap() hit `/api/auth/*` on the Django
 *       backend. Session lives in HTTP cookies (the API client sends
 *       credentials: "include"). State is rebuilt on app boot via /auth/me.
 *
 *   - **Mock mode** (VITE_AUTH_MOCK === "true"):
 *       login() accepts any non-empty username + password and persists a
 *       fake user to localStorage (so refreshes keep the session). Used
 *       during the migration window before /api/auth/* exists. The page is
 *       still gated when VITE_REQUIRE_AUTH=true — anyone can pick a name to
 *       sign in with, but at least they have to do something.
 *
 * To go live: set VITE_AUTH_MOCK=false (or unset) AND have Django serve
 * /api/auth/me, /api/auth/login, /api/auth/logout. No code changes needed.
 */
import { create } from "zustand";
import { get as apiGet, post as apiPost, ApiError } from "@/api/client";

const MOCK_MODE = import.meta.env.VITE_AUTH_MOCK === "true";
const MOCK_STORAGE_KEY = "opus.auth.mock:v1";
const MOCK_STORAGE_KEY_V0 = "opus.auth.mock";

/** Shape returned by /api/auth/me — keep loose for now. */
export interface CurrentUser {
  id: string;
  username: string;
  fullName: string;
  email: string;
  isStaff: boolean;
  roles: string[];
  /** True when the user has at least one *active* Meraki key registered.
   *  False until they visit /keys and register one. Pages that need a
   *  Meraki call should check this and route to /keys when missing —
   *  otherwise the API will 412. */
  merakiKeyConfigured?: boolean;
  /** Same idea for Aruba Central. False until the user uploads a
   *  bundle on /aruba/keys. Aruba pages that fan out to Central should
   *  gate on this to avoid silent 412s. */
  arubaKeyConfigured?: boolean;
  /** ISO timestamp of first-login welcome dismissal, or null if the
   *  user has never seen the orientation modal. The Overview page reads
   *  this to decide whether to show the welcome modal once. */
  welcomedAt?: string | null;
  /** Per-user AI-agent master switch (set by an admin on /users).
   *  False = /api/ai/chat is 403 regardless of role. Surfaced so the
   *  profile page can tell the user why the agent is disabled. */
  agentEnabled?: boolean;
}

interface AuthState {
  user: CurrentUser | null;
  /** "idle" before bootstrap, "loading" mid-call, "ready" after first answer. */
  status: "idle" | "loading" | "ready";
  error: string | null;
  sessionExpiryNotice: {
    reason: string;
    startedAt: number;
  } | null;

  /** Fetch the current user from the server (call once on app boot). */
  bootstrap: () => Promise<void>;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  /** Stamp welcomedAt server-side and locally so the welcome modal
   *  doesn't reopen on the next page load. Idempotent. */
  markWelcomed: () => Promise<void>;
  beginSessionExpiry: (reason?: string) => void;
  clearSessionExpiry: () => void;
}

/* ---------- mock-mode helpers ---------- */

function readMockUser(): CurrentUser | null {
  try {
    const raw = localStorage.getItem(MOCK_STORAGE_KEY)
      ?? localStorage.getItem(MOCK_STORAGE_KEY_V0); // migrate old key
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // Defensive: ensure it has the required shape.
    if (parsed && typeof parsed.username === "string") return parsed as CurrentUser;
    return null;
  } catch {
    return null;
  }
}

function writeMockUser(u: CurrentUser): void {
  try {
    localStorage.setItem(MOCK_STORAGE_KEY, JSON.stringify(u));
  } catch {
    // localStorage may be unavailable in private mode — login still "works"
    // for the duration of the page lifetime via the in-memory store.
  }
}

function clearMockUser(): void {
  try {
    localStorage.removeItem(MOCK_STORAGE_KEY);
    localStorage.removeItem(MOCK_STORAGE_KEY_V0); // clear old key too
  } catch {
    // ignore
  }
}

/** Build a mock user from a username string. Roles + email are synthesized. */
function makeMockUser(username: string): CurrentUser {
  const trimmed = username.trim();
  return {
    id: `mock-${trimmed}`,
    username: trimmed,
    fullName: trimmed.replace(/[._-]/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
    email: trimmed.includes("@") ? trimmed : `${trimmed}@guest-tek.com`,
    isStaff: true,
    roles: ["mock"],
  };
}

/* ---------- store ---------- */

export const useAuth = create<AuthState>((set) => ({
  user: null,
  status: "idle",
  error: null,
  sessionExpiryNotice: null,

  bootstrap: async () => {
    set({ status: "loading", error: null });

    if (MOCK_MODE) {
      // Mock mode — restore from localStorage so a hard refresh keeps the
      // user signed in. No network call.
      const cached = readMockUser();
      set({ user: cached, status: "ready", sessionExpiryNotice: null });
      return;
    }

    try {
      const me = await apiGet<CurrentUser>("/auth/me");
      set({ user: me, status: "ready", sessionExpiryNotice: null });
    } catch (err) {
      // 401/404 are normal unauthenticated states during login flow.
      // 404 can happen in environments where /auth/me is not wired yet.
      if (err instanceof ApiError && (err.status === 401 || err.status === 404)) {
        set({ user: null, status: "ready", sessionExpiryNotice: null });
        return;
      }
      set({
        user: null,
        status: "ready",
        error: err instanceof Error ? err.message : "unknown error",
      });
    }
  },

  login: async (username, password) => {
    set({ status: "loading", error: null });

    if (MOCK_MODE) {
      // Accept any non-empty creds — schema validation already rejected blanks.
      // The password isn't checked because there's no backend to check it against.
      if (!username.trim() || !password.trim()) {
        set({ status: "ready", error: "Username and password are required" });
        throw new Error("blank credentials");
      }
      const u = makeMockUser(username);
      writeMockUser(u);
      set({ user: u, status: "ready", sessionExpiryNotice: null });
      return;
    }

    try {
      const me = await apiPost<CurrentUser>("/auth/login", { username, password });
      set({ user: me, status: "ready", sessionExpiryNotice: null });
    } catch (err) {
      set({
        status: "ready",
        error:
          err instanceof ApiError
            ? `Login failed (${err.status})`
            : err instanceof Error
              ? err.message
              : "unknown error",
      });
      throw err;
    }
  },

  logout: async () => {
    if (MOCK_MODE) {
      clearMockUser();
      set({ user: null, status: "ready", error: null, sessionExpiryNotice: null });
      return;
    }
    try {
      await apiPost("/auth/logout");
    } finally {
      // Always wipe local state — even if the server logout call fails the
      // user wants to be logged out client-side.
      set({ user: null, status: "ready", error: null, sessionExpiryNotice: null });
    }
  },

  markWelcomed: async () => {
    // Optimistic local stamp so the modal closes instantly even if the
    // server call is slow. The server returns the updated user; we re-set
    // from that to stay authoritative.
    const now = new Date().toISOString();
    set((s) => (s.user ? { user: { ...s.user, welcomedAt: now } } : s));
    if (MOCK_MODE) {
      const u = readMockUser();
      if (u) writeMockUser({ ...u, welcomedAt: now });
      return;
    }
    try {
      const refreshed = await apiPost<CurrentUser>("/auth/me/mark-welcomed");
      set({ user: refreshed });
    } catch {
      // Silent: the optimistic update is fine for the session. Next
      // bootstrap will reconcile from the server (welcomedAt still null
      // there → modal reopens on next visit, which is the right behavior
      // since the stamp didn't persist).
    }
  },

  beginSessionExpiry: (reason) => {
    set((state) => {
      if (state.sessionExpiryNotice) return state;
      return {
        error: reason ?? "Your session has expired. You will be logged out shortly.",
        sessionExpiryNotice: {
          reason: reason ?? "Your session has expired. You will be logged out shortly.",
          startedAt: Date.now(),
        },
      };
    });
  },

  clearSessionExpiry: () => {
    set({ sessionExpiryNotice: null });
  },
}));

// NOTE: previously listened for 'opus-auth-expired' from the api client
// to auto-redirect on any 401. Removed because Secure-cookie-over-HTTP
// scenarios cause every call to 401, which trapped the user in a redirect
// loop after a "successful" login. Bootstrap's own 401 handling on
// /auth/me already covers the legitimate "you're not logged in" case.
